import { useCallback, useState } from 'react';
import { authApi, AuthUser } from '../../services/api';
import { logger } from '../../utils/logger';
import { useAuthBootstrap } from './useAuthBootstrap';
import { useAuthOidcSignIn } from './useAuthOidcSignIn';
import { useAuthTokenLifecycle } from './useAuthTokenLifecycle';
import { AuthState, initialAuthState } from './auth.types';

export function useAuthProviderState() {
  const [state, setState] = useState<AuthState>(initialAuthState);

  const { clearRefreshTimer, scheduleTokenRefresh, applyValidatedSession, clearSession } =
    useAuthTokenLifecycle();

  const handleRefreshFailed = useCallback(() => {
    setState({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
      needsNiceLink: true,
      error: 'NICE sign-in expired. Sign in again to continue.',
    });
  }, []);

  const handleTokenRefreshed = useCallback(
    (accessToken: string, expiresIn: number) => {
      setState((prev) => ({ ...prev, accessToken }));
      scheduleTokenRefresh(expiresIn, handleTokenRefreshed, handleRefreshFailed);
    },
    [handleRefreshFailed, scheduleTokenRefresh]
  );

  const setAuthenticated = useCallback(
    (accessToken: string, expiresIn: number, user: AuthUser, sessionToken?: string) => {
      applyValidatedSession(accessToken, expiresIn, user, sessionToken);
      setState({
        user,
        accessToken,
        isAuthenticated: true,
        isLoading: false,
        needsNiceLink: false,
        error: null,
      });
      scheduleTokenRefresh(expiresIn, handleTokenRefreshed, handleRefreshFailed);
    },
    [applyValidatedSession, handleRefreshFailed, handleTokenRefreshed, scheduleTokenRefresh]
  );

  const bootstrapHandlers = {
    setLoading: () => setState((prev) => ({ ...prev, isLoading: true, error: null })),
    setBlockedOutsideSap: () =>
      setState({
        user: null,
        accessToken: null,
        isAuthenticated: false,
        isLoading: false,
        needsNiceLink: false,
        error: 'This application must be accessed from within SAP C4C.',
      }),
    setAuthenticated,
    setNeedsNiceLink: (error: string | null) =>
      setState({
        user: null,
        accessToken: null,
        isAuthenticated: false,
        isLoading: false,
        needsNiceLink: true,
        error,
      }),
    setFatalError: (error: string) =>
      setState({
        user: null,
        accessToken: null,
        isAuthenticated: false,
        isLoading: false,
        needsNiceLink: false,
        error,
      }),
  };

  const { authBootstrapRef, resolveAuthContext, bootstrapAuth } =
    useAuthBootstrap(bootstrapHandlers);
  const { signInWithNice } = useAuthOidcSignIn(
    authBootstrapRef,
    resolveAuthContext,
    setAuthenticated
  );

  const refreshToken = useCallback(async () => {
    const response = await authApi.refreshToken();
    handleTokenRefreshed(response.accessToken, response.expiresIn);
  }, [handleTokenRefreshed]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (err) {
      logger.error('[Auth] Logout error:', err);
    } finally {
      clearSession();
      setState({
        user: null,
        accessToken: null,
        isAuthenticated: false,
        isLoading: false,
        needsNiceLink: true,
        error: null,
      });
    }
  }, [clearSession]);

  return {
    state,
    signInWithNice,
    logout,
    refreshToken,
    bootstrapAuth,
    clearRefreshTimer,
  };
}
